import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ArrowUp, CircleAlert, CircleCheck, Info, RefreshCw, RotateCcw, TriangleAlert, X } from 'lucide-react';
import type { AssistantDigest, EmployeeDetail, SearchResults } from '@skhub/contracts';
import { api } from '@/lib/api';
import { useAssistantDigest } from '@/lib/queries';
import { relTime } from '@/lib/format';
import { answer, healthTiles, lookupAnswer, SUGGESTIONS, type Answer, type AnswerRow, type Tone } from './answer';

/**
 * IT Assistant: a floating launcher (bottom-right) that opens a chat over the live register.
 * Every answer is built from GET /assistant/digest (SQL), so the numbers match the registers.
 */

interface Message {
  id: number;
  role: 'user' | 'bot';
  text?: string;
  question?: string;
  answer?: Answer;
  /** Waiting for the digest (or the short "thinking" beat) before answering. */
  pending?: boolean;
}

const TONE_COLOR: Record<Tone, string> = {
  danger: 'var(--danger)',
  warn: 'var(--warn)',
  ok: 'var(--st-available)',
  info: 'var(--st-assigned)',
  neutral: 'var(--text-3)',
};
const TONE_ICON = { danger: CircleAlert, warn: TriangleAlert, ok: CircleCheck, info: Info, neutral: Info } as const;

const ROWS_SHOWN = 8;
const THINK_MS = 420;
const SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.8 } as const;

let nextId = 1;

export function AssistantLauncher() {
  const [open, setOpen] = useState(false);
  const fabRef = useRef<HTMLButtonElement>(null);
  const digest = useAssistantDigest(true);
  const [messages, setMessages] = useState<Message[]>([]);

  const flagged = useMemo(() => (digest.data ? healthTiles(digest.data).filter((t) => t.count > 0 && (t.tone === 'danger' || t.tone === 'warn')).length : 0), [digest.data]);

  const ask = (question: string) => {
    const q = question.trim();
    if (!q) return;
    setMessages((m) => [...m, { id: nextId++, role: 'user', text: q }, { id: nextId++, role: 'bot', question: q, pending: true }]);
  };

  // Resolve pending questions once the digest is in hand, after at least a short "thinking" beat.
  const data = digest.data;
  const inFlight = useRef(new Set<number>());
  useEffect(() => {
    if (!data) return;
    for (const m of messages) {
      if (!m.pending || inFlight.current.has(m.id)) continue;
      inFlight.current.add(m.id);
      const started = Date.now();
      void resolveAnswer(m.question!, data).then(async (a) => {
        const wait = THINK_MS - (Date.now() - started);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, pending: false, answer: a } : x)));
        inFlight.current.delete(m.id);
      });
    }
  }, [data, messages]);

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            ref={fabRef}
            type="button"
            onClick={() => setOpen(true)}
            className="assistant-fab group fixed right-5 bottom-5 z-40 print:hidden"
            aria-label={`Open IT Assistant${flagged ? `: ${flagged} checks need attention` : ''}`}
            initial={{ opacity: 0, scale: 0.6, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 12, transition: { duration: 0.14 } }}
            transition={{ ...SPRING, delay: 0.35 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.94 }}
          >
            <span className="assistant-fab-label" aria-hidden>
              Ask IT Assistant
            </span>
            <span className="assistant-fab-orb">
              <BotGlyph className="size-7" />
            </span>
            {flagged > 0 && (
              <span aria-hidden className="assistant-fab-badge tnum">
                {flagged}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="animate-fade assistant-overlay fixed inset-0 z-50" />
          <Dialog.Content
            className="assistant-panel fixed z-50 flex flex-col overflow-hidden focus:outline-none"
            // The launcher is not rendered while the panel is open, so Radix has nothing to return focus to: do it here.
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              requestAnimationFrame(() => fabRef.current?.focus());
            }}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              (document.getElementById('assistant-input') as HTMLInputElement | null)?.focus();
            }}
          >
            <Header digest={digest.data} fetching={digest.isFetching} onRefresh={() => void digest.refetch()} onReset={messages.length ? () => setMessages([]) : undefined} />
            <Conversation
              digest={digest.data}
              error={digest.error}
              loading={digest.isPending}
              onRetry={() => void digest.refetch()}
              messages={messages}
              onAsk={ask}
              onNavigate={() => setOpen(false)}
            />
            <Composer onAsk={ask} showSuggestions={messages.length === 0} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

/**
 * A check is answered from the digest. Anything else (a name, employee ID, asset tag, serial or product) is
 * looked up in the register; one person found opens their record (devices, seats, other IDs).
 */
async function resolveAnswer(question: string, data: AssistantDigest): Promise<Answer> {
  const a = answer(question, data);
  if (!a.lookup) return a;
  const term = a.lookup;
  const fallback = { ...a, lookup: undefined };
  const empty = (r: SearchResults) => !r.employees.length && !r.assets.length && !r.licenses?.length;
  try {
    const search = (q: string) => api.get<SearchResults>('/search', { q });
    let r = await search(term);
    // "Shrivinayak Dhotre laptop serial" may not match as one phrase: try the longest words on their own.
    const words = term.split(/s+/).filter((w) => w.length >= 3).sort((x, y) => y.length - x.length);
    if (empty(r) && words.length > 1) {
      for (const w of words.slice(0, 3)) {
        const next = await search(w);
        if (!empty(next)) {
          r = next;
          break;
        }
      }
    }
    const person = r.employees.length === 1 ? await api.get<EmployeeDetail>(`/employees/${r.employees[0]!.id}`).catch(() => undefined) : undefined;
    return lookupAnswer(term, r, person);
  } catch {
    return fallback;
  }
}

// ── Header ───────────────────────────────────────────────────

function Header({ digest, fetching, onRefresh, onReset }: { digest?: AssistantDigest; fetching: boolean; onRefresh: () => void; onReset?: () => void }) {
  return (
    <div className="assistant-head scope-dark relative shrink-0 px-4 pt-4 pb-3.5 text-white">
      <div className="flex items-center gap-3">
        <span className="assistant-avatar">
          <BotGlyph className="size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <Dialog.Title className="text-[15px] leading-tight font-semibold tracking-[-0.01em] text-white">IT Assistant</Dialog.Title>
          <Dialog.Description className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-fg-2">
            <span aria-hidden className={`size-1.5 shrink-0 rounded-full ${fetching ? 'animate-[pulse-dot_1s_ease-in-out_infinite] bg-[var(--warn)]' : 'bg-[var(--st-available)]'}`} />
            {fetching ? 'Checking the live register…' : digest ? `Live register · checked ${relTime(digest.generatedAt)}` : 'Connecting to the register…'}
          </Dialog.Description>
        </div>
        {onReset && (
          <button type="button" className="btn btn-ghost btn-icon text-fg-2 hover:text-white" aria-label="Start a new conversation" title="New conversation" onClick={onReset}>
            <RotateCcw aria-hidden className="size-4" />
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-icon text-fg-2 hover:text-white" aria-label="Re-run all checks" title="Re-run checks" onClick={onRefresh} disabled={fetching}>
          <RefreshCw aria-hidden className={`size-4 ${fetching ? 'animate-spin' : ''}`} />
        </button>
        <Dialog.Close className="btn btn-ghost btn-icon text-fg-2 hover:text-white" aria-label="Close IT Assistant">
          <X aria-hidden className="size-4" />
        </Dialog.Close>
      </div>
    </div>
  );
}

// ── Conversation ─────────────────────────────────────────────

function Conversation({
  digest,
  error,
  loading,
  onRetry,
  messages,
  onAsk,
  onNavigate,
}: {
  digest?: AssistantDigest;
  error: unknown;
  loading: boolean;
  onRetry: () => void;
  messages: Message[];
  onAsk: (q: string) => void;
  onNavigate: () => void;
}) {
  const end = useRef<HTMLDivElement>(null);
  const last = messages[messages.length - 1];
  // While thinking, follow the bottom; once answered, bring the question to the top so a long answer reads from its start.
  useEffect(() => {
    if (!last) return;
    const question = last.pending ? null : document.getElementById(`assistant-msg-${last.id - 1}`);
    if (question) question.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [last?.id, last?.pending]);

  return (
    <div className="assistant-scroll flex-1 overflow-y-auto overscroll-contain px-4 py-4" role="log" aria-live="polite" aria-label="Conversation with IT Assistant">
      <Welcome digest={digest} loading={loading} error={error} onRetry={onRetry} onAsk={onAsk} />
      {messages.map((m) =>
        m.role === 'user' ? (
          <motion.div key={m.id} id={`assistant-msg-${m.id}`} className="mt-4 flex scroll-mt-3 justify-end" initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={SPRING}>
            <p className="assistant-bubble-user max-w-[85%]">{m.text}</p>
          </motion.div>
        ) : (
          <div key={m.id} className="mt-3">
            {m.pending ? (
              error ? (
                <BotNote>
                  I could not reach the register to answer that.{' '}
                  <button type="button" className="font-medium text-brand-ink underline-offset-2 hover:underline" onClick={onRetry}>
                    Try again
                  </button>
                </BotNote>
              ) : (
                <Typing />
              )
            ) : (
              m.answer && <AnswerCard answer={m.answer} onAsk={onAsk} onNavigate={onNavigate} />
            )}
          </div>
        ),
      )}
      <div ref={end} />
    </div>
  );
}

function Welcome({ digest, loading, error, onRetry, onAsk }: { digest?: AssistantDigest; loading: boolean; error: unknown; onRetry: () => void; onAsk: (q: string) => void }) {
  const tiles = digest ? healthTiles(digest) : null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={SPRING}>
      <p className="text-[13px] leading-relaxed text-fg-2">
        Hi, I keep watch over the register. Here is what my checks found just now. Tap any check for the exact records, or ask me in your own words.
      </p>
      {error && !digest ? (
        <BotNote>
          The checks could not run.{' '}
          <button type="button" className="font-medium text-brand-ink underline-offset-2 hover:underline" onClick={onRetry}>
            Try again
          </button>
        </BotNote>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {loading || !tiles
            ? Array.from({ length: 9 }, (_, i) => <div key={i} className="assistant-tile h-[74px] animate-pulse" />)
            : tiles.map((t, i) => (
                <motion.button
                  key={t.key}
                  type="button"
                  className="assistant-tile text-left"
                  style={{ ['--tone' as string]: TONE_COLOR[t.tone] }}
                  onClick={() => onAsk(t.ask)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...SPRING, delay: 0.04 * i }}
                >
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden className="size-1.5 rounded-full" style={{ background: 'var(--tone)' }} />
                    <span className="text-[18px] leading-none font-semibold tracking-[-0.02em] text-fg tnum">{t.count}</span>
                  </span>
                  <span className="mt-1.5 block text-xs leading-tight text-fg-2">{t.label}</span>
                </motion.button>
              ))}
        </div>
      )}
    </motion.div>
  );
}

function BotNote({ children }: { children: React.ReactNode }) {
  return <div className="assistant-card mt-3 px-3.5 py-3 text-[13px] text-fg-2">{children}</div>;
}

function Typing() {
  return (
    <motion.div role="status" className="assistant-card inline-flex items-center gap-1 px-3.5 py-3" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={SPRING} aria-label="IT Assistant is checking">
      {[0, 1, 2].map((i) => (
        <span key={i} className="assistant-dot" style={{ animationDelay: `${i * 140}ms` }} />
      ))}
    </motion.div>
  );
}

function AnswerCard({ answer: a, onAsk, onNavigate }: { answer: Answer; onAsk: (q: string) => void; onNavigate: () => void }) {
  const [all, setAll] = useState(false);
  const tone = a.tone ?? 'neutral';
  const Icon = TONE_ICON[tone];
  const total = a.sections.reduce((s, x) => s + x.rows.length, 0);
  let budget = all ? Infinity : ROWS_SHOWN;
  let rowIndex = 0;

  return (
    <motion.div className="assistant-card overflow-hidden" style={{ ['--tone' as string]: TONE_COLOR[tone] }} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={SPRING}>
      <div className="assistant-card-accent px-3.5 pt-3 pb-2.5">
        <div className="flex items-center gap-2">
          <Icon aria-hidden className="size-4 shrink-0" style={{ color: 'var(--tone)' }} />
          <p className="min-w-0 flex-1 text-[13.5px] font-semibold text-fg">{a.title}</p>
          {a.count !== undefined && (
            <span className="rounded-full px-2 py-0.5 text-xs font-semibold tnum" style={{ color: 'var(--tone)', background: 'color-mix(in srgb, var(--tone) 13%, transparent)' }}>
              {a.count}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-fg-2">{a.text}</p>
      </div>

      {total > 0 && (
        <div className="border-t border-line">
          {a.sections.map((s, si) => {
            if (budget <= 0) return null;
            const rows = s.rows.slice(0, budget);
            budget -= rows.length;
            return (
              <div key={si}>
                {s.heading && <p className="eyebrow border-b border-line bg-surface-2/60 px-3.5 py-1.5 normal-case tracking-normal">{s.heading}</p>}
                <ul className="divide-y divide-line">
                  {rows.map((r) => (
                    <ResultRow key={r.key} row={r} index={rowIndex++} onAsk={onAsk} onNavigate={onNavigate} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {(total > ROWS_SHOWN || a.link) && (
        <div className="flex items-center justify-between gap-2 border-t border-line px-3.5 py-2">
          {total > ROWS_SHOWN ? (
            <button type="button" className="text-xs font-medium text-brand-ink hover:underline" onClick={() => setAll((v) => !v)}>
              {all ? 'Show fewer' : `Show all ${total}`}
            </button>
          ) : (
            <span />
          )}
          {a.link && (
            <Link to={a.link.to} onClick={onNavigate} className="group inline-flex items-center gap-1 text-xs font-medium text-fg-2 hover:text-fg">
              {a.link.label}
              <ArrowRight aria-hidden className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      )}

      {a.followUps && a.followUps.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-line px-3.5 py-2.5">
          {a.followUps.map((f) => (
            <button key={f} type="button" className="assistant-chip" onClick={() => onAsk(f)}>
              {f}
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function ResultRow({ row: r, index, onAsk, onNavigate }: { row: AnswerRow; index: number; onAsk: (q: string) => void; onNavigate: () => void }) {
  const tone = TONE_COLOR[r.tone ?? 'neutral'];
  // Rows from a health summary carry the question to ask instead of a link.
  const askChip = !r.to && r.chips?.length === 1 ? r.chips[0] : undefined;
  const body = (
    <>
      <span aria-hidden className="mt-1.5 size-1.5 shrink-0 rounded-full" style={{ background: tone }} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          {r.code && <span className="mono shrink-0 text-[12px] text-fg-2">{r.code}</span>}
          <span className="truncate text-[13px] font-medium text-fg">{r.primary}</span>
        </span>
        {r.secondary && <span className="mt-0.5 block truncate text-xs text-fg-3">{r.secondary}</span>}
        {!askChip && r.chips && r.chips.length > 0 && (
          <span className="mt-1.5 flex flex-wrap gap-1">
            {r.chips.map((c) =>
              r.to ? (
                <span key={c} className="mono rounded-[5px] bg-surface-2 px-1.5 py-px text-[11px] text-fg-2">
                  {c}
                </span>
              ) : (
                <button key={c} type="button" className="assistant-chip" onClick={() => onAsk(c)}>
                  {c}
                </button>
              ),
            )}
          </span>
        )}
      </span>
      {r.badge && (
        <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap tnum" style={{ color: tone, background: `color-mix(in srgb, ${tone} 12%, transparent)` }}>
          {r.badge}
        </span>
      )}
    </>
  );
  const cls = 'assistant-row flex items-start gap-2.5 px-3.5 py-2.5';
  return (
    <motion.li initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ ...SPRING, delay: Math.min(index, 10) * 0.03 }}>
      {r.to ? (
        <Link to={r.to} onClick={onNavigate} className={cls}>
          {body}
        </Link>
      ) : askChip ? (
        <button type="button" onClick={() => onAsk(askChip)} className={`${cls} w-full text-left`}>
          {body}
        </button>
      ) : (
        <div className={cls}>{body}</div>
      )}
    </motion.li>
  );
}

// ── Composer ─────────────────────────────────────────────────

function Composer({ onAsk, showSuggestions }: { onAsk: (q: string) => void; showSuggestions: boolean }) {
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onAsk(text);
    setText('');
  };
  return (
    <div className="shrink-0 border-t border-line bg-surface px-3 pt-2.5 pb-3">
      {showSuggestions && (
        <div className="assistant-suggestions -mx-3 mb-2.5 flex gap-1.5 overflow-x-auto px-3 pb-0.5">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" className="assistant-chip shrink-0" onClick={() => onAsk(s)}>
              {s}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="assistant-input flex items-center gap-2 pr-1.5 pl-3.5">
        <label htmlFor="assistant-input" className="sr-only">
          Ask the IT Assistant
        </label>
        <input
          id="assistant-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask a check, or name a person, asset or licence…"
          autoComplete="off"
          maxLength={300}
          className="h-11 min-w-0 flex-1 bg-transparent text-[13.5px] text-fg outline-none placeholder:text-fg-3"
        />
        <button type="submit" className="assistant-send" disabled={!text.trim()} aria-label="Send question">
          <ArrowUp aria-hidden className="size-4" strokeWidth={2.25} />
        </button>
      </form>
      <p className="mt-1.5 px-1 text-[11px] text-fg-3">Answers come straight from the live register. Nothing is changed.</p>
    </div>
  );
}

// ── Glyph ────────────────────────────────────────────────────

/** The assistant's face: a rounded visor with two eyes and an antenna spark. */
function BotGlyph({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden className={className}>
      <path d="M16 4.5v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="16" cy="4" r="1.9" fill="#ff8a57" className="assistant-spark" />
      <rect x="5" y="8.5" width="22" height="17" rx="7" stroke="currentColor" strokeWidth="1.8" />
      <rect x="9" y="13" width="14" height="7.5" rx="3.75" fill="currentColor" fillOpacity="0.14" />
      <circle cx="12.75" cy="16.75" r="1.7" fill="currentColor" className="assistant-eye" />
      <circle cx="19.25" cy="16.75" r="1.7" fill="currentColor" className="assistant-eye" />
      <path d="M3 15v4M29 15v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
